<script setup lang="ts">
import type { AcMilestone } from '@/types/crm';
import { useGetDocs, postData, updateData, deleteData } from '../../mixins/modularFirebaseOperations';
import { ref, computed } from 'vue';
import { modalClosed } from '../../mixins/visualFormatters';
import addMilestoneTypeForm from '../crm-components/addMilestoneTypeForm.vue';
import acMediumModal from '../reusable/teleports/acMediumModal.vue';
import skeletonTable from '../reusable/skeletonTable.vue';
const { docs, isLoadingDocs } =  useGetDocs<AcMilestone>("crm/milestones/list")
const isLoadingMileStones = ref(false)

const isAddingMilestone = ref(false)
const editedMilestone = ref<AcMilestone | undefined>(undefined)
const milestoneCategories = computed(() => [...new Set(docs.value.map(doc => doc.category))])

const groupedMilestones = computed(() => {
    const groups: Record<string, AcMilestone[]> = {}
    docs.value.forEach(milestone => {
        if (!groups[milestone.category]) {
            groups[milestone.category] = []
        }
        groups[milestone.category].push(milestone)
    })
    return groups
})

const handleMilestoneTypeAdded = async (milestoneType: AcMilestone) => {
    try {
        const docRef = await postData(milestoneType, "crm/milestones/list")
        docs.value.push({
            id: docRef?.id ?? "",
            ...milestoneType,
        })
    } catch (error) {
        console.error(error)
    }
    finally {
        isAddingMilestone.value = false
        modalClosed()
    }
}

const handleMilestoneTypeUpdated = async (milestoneType: AcMilestone) => {
    try {
        await updateData("crm/milestones/list", milestoneType!.id!, milestoneType)
        const index = docs.value.findIndex(doc => doc.id === milestoneType.id)
        docs.value[index] = milestoneType
    } catch (error) {
        console.error(error)
    }
    finally {
        editedMilestone.value = undefined
        modalClosed()
    }
}

const handleMilestoneTypeDeleted = async (milestoneType: AcMilestone) => {
    try {
        await deleteData("crm/milestones/list", milestoneType!.id!)
        const index = docs.value.findIndex(doc => doc.id === milestoneType.id)
        docs.value.splice(index, 1)
    } catch (error) {
        console.error(error)

    }
}

const handleClosed = () => {
    isAddingMilestone.value = false
    editedMilestone.value = undefined
    modalClosed()
}

</script>

<template>
    <div class="main-container content-start mt-8">
        <header class="action-header  col-start-1 col-end-13 flex items-center justify-between">
            <h2 class="mb-0 ac-large-title">Temas Başlıkları</h2>
            <button class="ac-add-button" @click="isAddingMilestone = true">Temas Türü Ekle</button>
        </header>

    
        <skeleton-table :width="100" class="col-start-1 col-end-13" :rows="15" v-if="isLoadingDocs || isLoadingMileStones" />
        <table class="alt-table col-start-1 col-end-13 w-full" v-else>
            <tbody>
                <!-- eslint-disable-next-line vue/no-v-for-template-key -->
                <template v-for="(milestones, category) in groupedMilestones" :key="category">
                    <!-- Category Header Row -->
                    <tr class="category-header">
                        <td colspan="3" class="font-bold bg-[var(--box-color)] border-b-2">
                            {{ category }}
                        </td>
                    </tr>
                    <!-- Milestone Rows for this Category -->
                    <tr class="vertical-center" v-for="milestone in milestones" :key="milestone.id">
                        <td class="w-6" ><span class="material-icons">{{ milestone.icon
                                }}</span></td>
                        <td>{{ milestone.title }}</td>
                        <td class="flex items-center justify-end gap-8">
                            <button @click="editedMilestone = milestone" class="ac-table-button">
                                Düzenle
                            </button>
                            <button @click="handleMilestoneTypeDeleted(milestone)"
                                class="ac-table-button text-red-400 hover:text-red-500">
                                Sil
                            </button>
                        </td>
                    </tr>
                </template>
            </tbody>
        </table>

        <teleport to="body">
            <transition appear type="animation" enter-active-class="animate__animated animate__fadeIn animate__fast"
                leave-active-class="animate__animated animate__fadeOut animate__faster">
                <ac-medium-modal @modal-closed="handleClosed" class="lg:!top-44 overflow-y-scroll"
                    v-if="isAddingMilestone || editedMilestone" title="Yeni Temas Kategorisi Ekle" width="50vw"
                    height="fit-content">
                    <add-milestone-type-form @milestone-type-added="handleMilestoneTypeAdded"
                        @milestone-type-updated="handleMilestoneTypeUpdated"
                        @cancelled="isAddingMilestone = false; editedMilestone = undefined; modalClosed()"
                        :existing-milestone-type="editedMilestone"
                        :existing-milestone-categories="milestoneCategories" />
                </ac-medium-modal>
            </transition>
        </teleport>


    </div>

</template>